import { useStages } from "@/context/StagesContext";
import { getFormattedDate } from "@/helpers/date.helper";
import { UserChallengeStatus } from "@/services/challenges/types/challenges.enums";
import { ChallengeStageDetails } from "@/services/challenges/types/challenges.types";
import Theme from "@/utils/theme";
import { isBefore } from "date-fns";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRegistrationCompletion } from "@/hooks/useRegistrationCompletion";

export const useAccordionDetailItem = (
  stageId: number,
  {
    id,
    startDate,
    status,
    type,
    isOpenForStart,
    scheduledFor,
  }: ChallengeStageDetails
) => {
  const navigate = useNavigate();
  const { isValidStage, currentStage } = useStages();
  const { isRegistrationComplete } = useRegistrationCompletion();

  const isStarted = useMemo(() => {
    return isBefore(startDate, new Date()) && isOpenForStart;
  }, [startDate, isOpenForStart]);

  const statusText = useMemo(() => {
    if (isRegistrationComplete) {
      return {
        color: Theme.colors.feedback.positive,
        text: "Concluído",
      };
    }

    if (type === "InPersonChallenge") {
      if (status === UserChallengeStatus.FINISHED) {
        return {
          color: Theme.colors.feedback.positive,
          text: "Concluído",
        };
      }

      if (scheduledFor) {
        return {
          color: "#F9FAFA",
          textColor: Theme.colors.neutral.mid_dark,
          text: `Entrevista agendada:${"\n"}${getFormattedDate(
            scheduledFor,
            true
          )}`,
          type: "in-person",
        };
      }

      return {
        color: "#F9FAFA",
        textColor: Theme.colors.neutral.mid_dark,
        text: `Aguarde agendamento`,
        type: "in-person",
      };
    }

    if (!isStarted) {
      return {
        color: Theme.colors.neutral.light,
        textColor: Theme.colors.neutral.mid_dark,
        text: `Liberação dia ${getFormattedDate(startDate, true)}`,
      };
    }

    // API omits status when there is no submission row (undefined); do not treat as "Enviado".
    if (status == null) {
      return {
        color: Theme.colors.feedback.warning,
        text: "Pendente",
      };
    }

    if (status === UserChallengeStatus.CLOSED) {
      return {
        color: Theme.colors.neutral.light,
        textColor: Theme.colors.neutral.mid_dark,
        text: "Indisponível",
      };
    }

    if (status === UserChallengeStatus.PENDING) {
      return {
        color: Theme.colors.feedback.warning,
        text: "Pendente",
      };
    }

    if (
      status === UserChallengeStatus.COMPLETED ||
      status === UserChallengeStatus.APPROVED
    ) {
      return {
        color: Theme.colors.feedback.positive,
        text: "Concluído",
      };
    }

    if (status === UserChallengeStatus.FINISHED) {
      if (type === "CaseChallenge") {
        return {
          color: Theme.colors.feedback.info,
          text: "Enviado",
        };
      }
      return {
        color: Theme.colors.feedback.positive,
        text: "Concluído",
      };
    }

    if (status === UserChallengeStatus.REJECTED) {
      return {
        color: Theme.colors.feedback.negative,
        text: "Reprovado",
      };
    }

    if (status === UserChallengeStatus.SENT) {
      return {
        color: Theme.colors.feedback.info,
        text: "Enviado",
      };
    }

    return {
      color: Theme.colors.feedback.warning,
      text: "Pendente",
    };
  }, [isStarted, status, type, scheduledFor, isRegistrationComplete]);

  const buttonType: "locked" | "points" | "waiting" | "pending" | "started" =
    useMemo(() => {
      if (type === "InPersonChallenge") {
        if (status === UserChallengeStatus.FINISHED) {
          return "points";
        }

        return "locked";
      }

      if (!isStarted) {
        return "locked";
      }

      if (isRegistrationComplete) {
        return "points";
      }

      const stageBlocked = currentStage?.id !== stageId || !isValidStage;

      // No submission row: user should start the challenge (primary CTA).
      if (status == null) {
        return stageBlocked ? "locked" : "pending";
      }

      if (status === UserChallengeStatus.CLOSED) {
        return "locked";
      }

      // Pending / sent / finished / etc.: user can open the challenge to view or continue.
      if (status === UserChallengeStatus.PENDING) {
        return stageBlocked ? "locked" : "started";
      }

      return "started";
    }, [
      type,
      isValidStage,
      isStarted,
      status,
      currentStage?.id,
      stageId,
      isRegistrationComplete,
    ]);

  const handleStartChallenge = () => {
    let queryType = "";
    switch (type) {
      case "MediaChallenge":
        queryType = "media";
        break;
      case "QuizChallenge":
        queryType = "quiz";
        break;

      case "CaseChallenge":
        queryType = "case";
        break;
    }

    navigate(`/challenges/${queryType}/${id}`);
  };

  return { statusText, buttonType, handleStartChallenge };
};
